import type { CartItemInterface, SelectedCartModifier } from "@/models/cart-item"
import type { CartLine, CartModifierGroupStored } from "@/store/cart/cart.types"
import { canonicalizeModifierGroups } from "@/store/cart/cart-line-identity"
import { newClientItemId } from "@/shared/ids/client-item-id"

export { newClientItemId }

function rowQuantityForPricing(m: { quantity?: number | null }, parentQty: number): number {
  if (m.quantity === null || m.quantity === undefined) {
    const pq = Math.max(0, Math.floor(Number(parentQty)))
    return pq > 0 ? pq : 1
  }
  const q = Math.max(0, Math.floor(Number(m.quantity)))
  return q > 0 ? q : 1
}

/**
 * For pricing / legacy order row builder (bridge id = SelectedCartModifier.itemId).
 */
export function cartLineToSelectedModifiers(
  line: Pick<CartLine, "selected_modifiers">,
  parentQty: number
): SelectedCartModifier[] {
  const groups = canonicalizeModifierGroups(line.selected_modifiers)
  const out: SelectedCartModifier[] = []
  for (const g of groups) {
    for (const m of g.items) {
      out.push({
        groupId: g.modifier_group_id,
        itemId: m.modifier_group_item_id,
        quantity: rowQuantityForPricing(m, parentQty),
        delta_price: m.delta_price,
      })
    }
  }
  return out
}

/** Client-side discount preview / legacy helpers */
export function cartLineToCartItemInterface(line: CartLine): CartItemInterface {
  const qty = Math.max(0, Math.floor(Number(line.quantity) || 0))
  return {
    count: qty,
    menu_item_id: line.menu_item_id,
    item_price: line.item_price,
    item_name: line.item_name,
    category: line.category,
    restaurant_id: line.restaurant_id,
    selectedModifiers: cartLineToSelectedModifiers(line, qty),
    selectedCombos: line.selectedCombos,
    comboTotalPerUnit: line.comboTotalPerUnit,
    addonsTotal: line.addonsTotal,
    itemTotal: line.itemTotal,
  }
}

export function legacySelectedModifiersToGroups(
  mods: SelectedCartModifier[] | undefined | null
): CartModifierGroupStored[] {
  const raw = mods ?? []
  if (raw.length === 0) return []
  const byGroup = new Map<string, CartModifierGroupStored["items"]>()
  for (const m of raw) {
    const gid = String(m.groupId ?? "").trim()
    const bridge = String(m.itemId ?? "").trim()
    if (!gid || !bridge) continue
    const q = m.quantity
    const row = {
      modifier_group_item_id: bridge,
      child_menu_item_id: "",
      quantity: q,
      delta_price: Number(m.delta_price) || 0,
    }
    const list = byGroup.get(gid) ?? []
    list.push(row)
    byGroup.set(gid, list)
  }
  return Array.from(byGroup.entries()).map(([modifier_group_id, items]) => ({
    modifier_group_id,
    items,
  }))
}

export function legacyCartItemToCartLine(item: CartItemInterface): CartLine {
  const qty = Math.max(0, Math.floor(Number(item.count) || 0))
  const base: CartLine = {
    client_item_id: newClientItemId(),
    menu_item_id: String(item.menu_item_id),
    quantity: qty,
    selected_modifiers: legacySelectedModifiersToGroups(item.selectedModifiers),
    item_name: item.item_name,
    item_price: item.item_price,
    category: item.category,
    restaurant_id: String(item.restaurant_id),
    selectedCombos: item.selectedCombos,
    comboTotalPerUnit: item.comboTotalPerUnit,
    addonsTotal: item.addonsTotal,
    itemTotal: item.itemTotal,
  }
  return base
}

/** Attach `child_menu_item_id` when missing, using catalog modifier_groups. */
export function enrichCartLinesChildMenuItemIds(
  lines: CartLine[],
  menuItems: Array<{
    menu_item_id: string | number
    modifier_groups?: Array<{
      modifier_group_id?: string
      items?: Array<{
        modifier_group_item_id?: string
        child_menu_item_id?: string
      }>
    }>
  }>
): CartLine[] {
  const menuById = new Map(menuItems.map((m) => [String(m.menu_item_id), m]))
  return lines.map((line) => {
    const menu = menuById.get(String(line.menu_item_id))
    if (!menu?.modifier_groups?.length) return line
    const groups = line.selected_modifiers.map((g) => ({
      ...g,
      items: g.items.map((m) => {
        if (String(m.child_menu_item_id ?? "").trim()) return m
        const mg = menu.modifier_groups?.find(
          (x) => String(x?.modifier_group_id ?? "") === g.modifier_group_id
        )
        const bridge = String(m.modifier_group_item_id ?? "")
        const hit = mg?.items?.find((it) => String(it?.modifier_group_item_id ?? "") === bridge)
        const child = String(hit?.child_menu_item_id ?? "").trim()
        return child ? { ...m, child_menu_item_id: child } : m
      }),
    }))
    return { ...line, selected_modifiers: groups }
  })
}
